import { ApolloProvider } from '@apollo/react-hooks';
import ApolloClient from 'apollo-boost';
import { setEndPoint } from 'core/api/core';
import ScrollToTop from 'core/components/common/ScrollToTop';
import { ILinksContext, LinksProvider } from 'core/components/linksContext';
import { ISettingsContext, SettingsProvider } from 'core/components/settingsContext';
import { SnackbarContainer } from 'core/components/snackbar';
import { createStyles } from 'core/style/injectSheet';
import { ITheme, IThemeOptions } from 'core/style/interfaces';
import awaitYandexMetrica from 'core/utils/awaitYandexMetrica';
import React, { Fragment } from 'react';
import { render as reactRender } from 'react-dom';
import { HashRouter, Link, Route, Switch } from 'react-router-dom';
import { compose, withProps } from 'recompose';
import Span from 'showcase/components/links/Span';
import TargetBlankLink from 'showcase/components/links/TargetBlankLink';
import ShowcaseThemeProvider, { createThemes } from 'showcase/style/ShowcaseThemeProvider';
import AuthModal from 'site/components/auth/AuthModal';
import { AuthProvider } from 'site/components/auth/AuthProvider';
import CourseList from 'site/components/courses/CourseList';
import productDetailFetchData from 'site/components/product/public/productDetailFetchData';
import productDetailHoc from 'site/components/product/public/productDetailHoc';
import RegPayModal from 'site/components/reg-pay-stepper/RegPayModal';
import { ICommonSettings } from './interfaces';

type IShowcaseOptions = ICommonSettings & {
    coursesList?: {
        filter?: {
            showPublic?: boolean;
        };
    };
    teachersPage?: string;
    accountPage?: string;
    faqPage?: string;
    userAgreement: string;
    personalAgreement: string;
    customYmId?: number;
    refCode?: string;
};

const productDetailStyles = createStyles({
    header: {
        backgroundColor: 'transparent',
    },
    footer: {
        backgroundColor: 'transparent',
    },
});

const productHeaderSectionStyles = (theme: ITheme) =>
    createStyles({
        title: {
            color: theme.colors.textBase,
        },
        shortDesc: {
            color: theme.colors.textBase,
        },
    });

const productFeaturesStyles = (theme: ITheme) =>
    createStyles({
        icon: {
            fill: theme.colors.textBase,
        },
    });

const notFoundStyles = (theme: ITheme) =>
    createStyles({
        background: {
            position: 'static',
            backgroundImage: 'none',
        },
        title: {
            color: theme.colors.contrastBg,
        },
        subtitle: {
            color: theme.colors.contrastBg,
        },
    });

export function render(options: IShowcaseOptions) {
    let { htmlelement } = options;
    const {
        endPoint = options.endPoint || LOCAL_SETTINGS.API_ENDPOINT,
        theme = {},
        coursesList = {},
        accountPage = '',
        teachersPage = '',
        faqPage = 'https://red-circule.com/page/chasto-zadavaemie-voprosi',
        userAgreement = 'https://red-circule.com/offer.docx',
        personalAgreement = 'https://red-circule.com/personal.docx',
        customYmId,
        refCode,
    } = options;

    if (typeof htmlelement === 'string') {
        htmlelement = document.querySelector(htmlelement);
    }

    // ожидаем получения яндекс метрики
    if (customYmId) awaitYandexMetrica(customYmId);

    const linksContextValue: ILinksContext = {
        courseList: {
            url: () => '/',
            Link,
        },
        productDetail: {
            url: id => `/courses/${id}`,
            Link,
        },
        teachers: {
            url: () => `${teachersPage}#/`,
            Link: teachersPage ? TargetBlankLink : Span,
        },
        teacherDetail: {
            url: id => `${teachersPage}#/${id}`,
            Link: teachersPage ? TargetBlankLink : Span,
        },
        account: {
            url: () => `${accountPage}#/`,
            Link: accountPage ? TargetBlankLink : Span,
        },
        accountProductDetail: {
            url: id => `${accountPage}#/courses/${id}`,
            Link: accountPage ? TargetBlankLink : Span,
        },
        faq: !!faqPage && {
            url: () => faqPage,
            Link: TargetBlankLink,
        },
        userAgreement: {
            url: () => userAgreement,
        },
        personalAgreement: {
            url: () => personalAgreement,
        },
        home: {
            url: () => '/',
            Link,
        },
    };

    const settingsContextValue: ISettingsContext = {
        contentProviderId: options.contentProviderId,
        coursesList: {
            ...coursesList,
            filter: {
                showPublic: false,
                ...coursesList.filter,
            },
        },
        showcaseRefCode: refCode,
        endPoint,
    };

    setEndPoint(endPoint);

    const ProductDetailContainer = compose(
        productDetailFetchData,
        withProps({ breadcrumbsContrast: false }),
    )(
        productDetailHoc({
            ContactsPreview: () => null,
        }),
    );

    const showcaseSpecificTheme: IThemeOptions = {
        ...theme,
        componentStyles: {
            ...theme.componentStyles,
            ProductDetail: {
                mode: 'partial',
                style: productDetailStyles,
            },
            NotFound: {
                mode: 'partial',
                style: notFoundStyles,
            },
            ProductFeatures: {
                mode: 'partial',
                style: productFeaturesStyles,
            },
            ProductHeaderSection: {
                mode: 'partial',
                style: productHeaderSectionStyles,
            },
        },
    };

    const client = new ApolloClient({
        uri: `${endPoint}/api/graphql/public`,
        credentials: 'include',
    });
    reactRender(
        <ApolloProvider client={client}>
            <AuthProvider>
                <ShowcaseThemeProvider {...createThemes(showcaseSpecificTheme)}>
                    <SettingsProvider value={settingsContextValue}>
                        <LinksProvider value={linksContextValue}>
                            <HashRouter basename="/">
                                <Fragment>
                                    <ScrollToTop />
                                    <AuthModal />
                                    <RegPayModal />
                                    <SnackbarContainer />
                                    <Switch>
                                        <Route path={linksContextValue.courseList.url()} exact component={CourseList} />
                                        <Route
                                            path={linksContextValue.productDetail.url(':productId')}
                                            component={ProductDetailContainer}
                                        />
                                    </Switch>
                                </Fragment>
                            </HashRouter>
                        </LinksProvider>
                    </SettingsProvider>
                </ShowcaseThemeProvider>
            </AuthProvider>
        </ApolloProvider>,
        htmlelement,
    );
}
